<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ApiClient from 'dashboard/api/ApiClient';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const mazamWebAPI = new ApiClient('mazam_web', { accountScoped: true });
const VPS_IP = '179.197.226.84';

export default {
  components: { PageHeader, NextButton },
  data() {
    return {
      channelName: '',
      agentBotId: '',
      domain: '',
      color: '#008069',
      siteTitle: '',
      logo: '',
      creating: false,
    };
  },
  computed: {
    ...mapGetters({ agentBots: 'agentBots/getBots' }),
    vpsIp() {
      return VPS_IP;
    },
    cleanDomain() {
      return this.domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/^www\./, '');
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
  },
  methods: {
    pickLogo(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 300 * 1024) {
        useAlert('Logo muito grande (máx. 300 KB). Use um PNG menor.');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.logo = reader.result;
      };
      reader.readAsDataURL(file);
    },
    async createChannel() {
      if (!this.channelName.trim()) {
        useAlert('Informe um nome para a caixa.');
        return;
      }
      this.creating = true;
      try {
        const { data } = await mazamWebAPI.create({
          name: this.channelName.trim(),
          agent_bot_id: this.agentBotId || null,
          domain: this.cleanDomain,
          site_name: this.channelName.trim(),
          theme: { brand: this.color },
          brand: {
            logo: this.logo,
            favicon: '',
            site_title: this.siteTitle.trim() || this.channelName.trim(),
          },
          start: {},
        });
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch (error) {
        useAlert('Não foi possível criar a caixa do site.');
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-auto">
    <PageHeader
      header-title="SITE WEB"
      header-content="Cria uma caixa de entrada web estilo WhatsApp servida no domínio que você escolher. Cores, logo e textos podem ser ajustados depois na aba Configuração do Site."
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          Nome da caixa
          <input v-model="channelName" type="text" placeholder="Ex.: Site Meu CLT" />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Domínio do site
          <input v-model="domain" type="text" placeholder="Ex.: meuclt.com" />
        </label>
        <div
          v-if="cleanDomain"
          class="mt-2 p-4 rounded-lg text-sm"
          style="background:rgba(29,155,240,.08);border:1px solid rgba(29,155,240,.35)"
        >
          <p class="font-semibold mb-2">📡 Apontamento no Cloudflare para {{ cleanDomain }}:</p>
          <ol class="list-decimal ml-5" style="line-height:1.9">
            <li>Registro <b>A</b> — nome <code>@</code> → conteúdo <code>{{ vpsIp }}</code></li>
            <li>Registro <b>CNAME</b> — nome <code>www</code> → conteúdo <code>{{ cleanDomain }}</code></li>
            <li>
              Deixe a nuvem <b>CINZA (DNS only)</b> até o site abrir com cadeado
              (certificado). Depois pode ligar o proxy laranja, se quiser.
            </li>
          </ol>
          <p class="mt-2 text-xs opacity-75">
            Sem o DNS apontado, o domínio não abre — o certificado é emitido
            automaticamente no primeiro acesso após a propagação.
          </p>
        </div>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Cor principal do site
          <div style="display:flex;align-items:center;gap:10px">
            <input v-model="color" type="color" style="width:52px;height:36px;padding:2px" />
            <input v-model="color" type="text" style="width:120px" class="!mb-0" />
            <span class="text-xs opacity-70">Verde WhatsApp: #008069 · Azul: #1d9bf0</span>
          </div>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Título da aba do navegador
          <input v-model="siteTitle" type="text" placeholder="Ex.: Meu CLT (vazio = nome da caixa)" />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Logomarca (PNG/JPG até 300 KB — usada no app instalado e ícones)
          <div style="display:flex;align-items:center;gap:12px">
            <img
              v-if="logo"
              :src="logo"
              alt="logo"
              style="width:44px;height:44px;border-radius:10px;object-fit:cover"
            />
            <input type="file" accept="image/png,image/jpeg" @change="pickLogo" />
          </div>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          IA (opcional)
          <select v-model="agentBotId">
            <option value="">Sem IA (só atendente humano)</option>
            <option v-for="bot in agentBots" :key="bot.id" :value="bot.id">
              {{ bot.name }}
            </option>
          </select>
        </label>
        <p class="help-text">
          A mesma IA usada nas caixas de WhatsApp. Pode trocar depois nas
          configurações da caixa.
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="creating"
          type="submit"
          solid
          blue
          label="Criar caixa do site"
        />
      </div>
    </form>
  </div>
</template>
